import { type HttpErrorResponse } from '@angular/common/http';
import { type TranslateService } from '@ngx-translate/core';

type ErrorPayload = {
  userMessageKey?: string;
  uiMessageKey?: string;
  message?: string;
  Message?: string;
};

type ResolveHttpErrorMessageOptions = {
  forbiddenKey?: string;
  fallbackKey?: string;
};

export const isTranslationKey = (value: string): boolean => /^[A-Z0-9_]+(?:\.[A-Z0-9_]+)+$/.test(value);

export const extractBackendUserFacingMessage = (error: HttpErrorResponse): string | undefined => {
  if (!error.error || typeof error.error !== 'object') {
    return undefined;
  }

  const payload = error.error as ErrorPayload;

  if (typeof payload.userMessageKey === 'string' && payload.userMessageKey.trim().length > 0) {
    return payload.userMessageKey;
  }

  if (typeof payload.uiMessageKey === 'string' && payload.uiMessageKey.trim().length > 0) {
    return payload.uiMessageKey;
  }

  if (typeof payload.message === 'string' && payload.message.trim().length > 0) {
    return payload.message;
  }

  if (typeof payload.Message === 'string' && payload.Message.trim().length > 0) {
    return payload.Message;
  }
  return undefined;
};

export const resolveHttpErrorMessage = (
  error: HttpErrorResponse,
  translateService: TranslateService,
  options: ResolveHttpErrorMessageOptions = {},
): string => {
  const {
    forbiddenKey = 'COMMON.ERRORS.403.REFRESH_PAGE',
    fallbackKey = 'COMMON.ERRORS.UNKNOWN',
  } = options;

  if (error.status === 403) {
    return translateService.instant(forbiddenKey);
  }

  // NOTE: Domain services are allowed to inspect backend payloads to decide
  // how best to represent errors to users. We keep the global UI handler
  // conservative (no raw free-text), but here we expose a safe extraction:
  // - If the backend provides a translation key, resolve it via i18n.
  // - Otherwise, return the backend free-text so domain components can
  //   display context-specific messages where appropriate.
  const backendMessage = extractBackendUserFacingMessage(error);
  if (backendMessage) {
    return isTranslationKey(backendMessage)
      ? translateService.instant(backendMessage)
      : backendMessage;
  }

  return translateService.instant(fallbackKey);
};
